import { StringIndexedDict } from "../../../../api/GenericTypes";
import {
  GenericEntityVirtualizedEntitySelectFormFieldProps,
  GenericVirtualizedEntitySelectFormFieldControllerWrapper,
} from "../../../formfields/GenericVirtualizedSelectFormField/GenericVirtualizedEntitySelectFormFieldControllerWrapper";
// import { QuickAddWrapper } from "../../QuickAdd/common/QuickAddWrapper";

import { useContext } from "react";
import { SessionContext } from "../../../contexts/SessionContext";
import {
  InventoryItemSuggestions,
  InventoryItemFilters,
  InventoryItemSortingOptionsConsts,
  inventoryItemsConstants,
  inventoriesConstants,
} from "../../../../api/Inventories";
import { QuickAddWrapper } from "../../QuickAdd/common/QuickAddWrapper";
import { QuickAddInventoryItemForm } from "../../QuickAdd/forms/QuickAddInventoryItemForm";
import { CustomField } from "../../../../api/CustomFields/CustomFields";
import { CustomTypeConstraintWrapper } from "../../../hierarchy/common/CustomTypeConstraintWrapper";
import { LoadingWrapper } from "../../../LoadingWrapper";
import { InventoryItemSelectModal } from "../../../../Inventories/InventoryItemSelectModal";
import { LucideIcon } from "../../../icon/LucideIcon";

interface InventoryItemsVirtualizedSelectFormProps<Entity extends StringIndexedDict>
  extends GenericEntityVirtualizedEntitySelectFormFieldProps<Entity, InventoryItemSuggestions, InventoryItemFilters> {
  customTypeConstraint?: CustomField["customTypeConstraint"];
  showBrowseButton?: boolean;
}

export const InventoryItemsVirtualizedSelectForm = <Entity extends StringIndexedDict>(
  props: InventoryItemsVirtualizedSelectFormProps<Entity>
) => {
  const { session } = useContext(SessionContext);
  // const disabled = props.disabled === undefined ? true : props.disabled;
  const showBrowseButton = props.showBrowseButton === undefined ? true : props.showBrowseButton;
  return (
    <GenericVirtualizedEntitySelectFormFieldControllerWrapper<Entity, InventoryItemSuggestions, InventoryItemFilters>
      {...props}
      filters={{
        sortBy: [{ property: "NAME", sortDirection: "ASC" }],
        ...props.filters,
        ...(Array.isArray(props.customTypeConstraint) &&
          !!props.customTypeConstraint.length && { customTypeIds: props.customTypeConstraint.map((c) => c.id) }),
      }}
      createModal={({ showModal, setShowModal, onCreate }) => (
        <QuickAddWrapper showModal={showModal} setShowModal={setShowModal} onCreate={onCreate}>
          {(quickAddProps) => (
            <QuickAddInventoryItemForm
              {...quickAddProps}
              initialValues={{
                ...quickAddProps.initialValues,
                ...(props.filters?.customTypeIds && {
                  customType: { name: "", id: props.filters?.customTypeIds?.[0] },
                }),
              }}
            />
          )}
        </QuickAddWrapper>
      )}
      entityConstants={inventoryItemsConstants}
      orderByOptions={InventoryItemSortingOptionsConsts}
      allowCreateEntity={session?.permissions.administer_custom_field_schemas && props.allowCreateEntity}
      disabled={props.disabled} // Disable the form to prevent the user from changing the value; User must use button
      prependChildren={({ value, onChange }) => (
        <>
          {showBrowseButton && (
            <CustomTypeConstraintWrapper customTypeConstraints={props.customTypeConstraint}>
              {({ inventoryIds, constraintIds, status, fetchStatus, error }) => (
                <LoadingWrapper status={status} fetchStatus={fetchStatus} error={error}>
                  <InventoryItemSelectModal
                    hierarchyRootTypeIds={inventoryIds}
                    triggerComponent={({ setShowModal }) => (
                      <button
                        className="btn btn-default"
                        onClick={() => setShowModal(true)}
                        title={`Browse ${inventoriesConstants.entityPlural}`}
                        style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 0 }}
                        disabled={props.disabled}
                      >
                        <LucideIcon name="folder-search" />
                      </button>
                    )}
                    onSelectCallback={onChange}
                    selectableCustomTypeIds={constraintIds}
                    isMulti={props.isMulti}
                    initialValues={value}
                  />
                </LoadingWrapper>
              )}
            </CustomTypeConstraintWrapper>
          )}
        </>
      )}
      style={{ ...props.style, gap: 0 }}
      selectFieldStyle={{ ...props.selectFieldStyle, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
    >
      {({ value, onChange }) =>
        props.children ? (
          typeof props.children === "function" ? (
            props.children({ value, onChange })
          ) : (
            <div style={{ marginLeft: 5 }}>{props.children}</div>
          )
        ) : null
      }
    </GenericVirtualizedEntitySelectFormFieldControllerWrapper>
  );
};
